import React, { useMemo, useRef, useState } from "react";
import { ApolloProvider } from "@apollo/client";
import { DateTime } from "luxon";
import { createPublicApolloClient } from "../../../apolloClient";
import { useGetCmpPublicEventPreCheckInQuery, useConfirmCmpPublicEventPreCheckInMutation } from "../../../graphql/types";

export const isEventPreCheckInPath = (path: string) => path === "/event-pre-check-in";
export const EventPreCheckInForm = () => {
  const inFlight = useRef(false);
  const input = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return { outboundEmailId: params.get("e") || "", credential: params.get("t") || "" };
  }, []);
  const [confirmed, setConfirmed] = useState(false);
  const [failure, setFailure] = useState("");
  const { data, loading, error } = useGetCmpPublicEventPreCheckInQuery({ variables: { input }, fetchPolicy: "no-cache",
    skip: !input.outboundEmailId || !input.credential });
  const [confirm, { loading: saving }] = useConfirmCmpPublicEventPreCheckInMutation({ fetchPolicy: "no-cache" });
  const details = data?.getCmpPublicEventPreCheckIn;
  const alreadyConfirmed = confirmed || !!details?.preCheckedInAtUtcMilli;
  const submit = async () => {
    if (inFlight.current || !details || saving || alreadyConfirmed || failure) return;
    inFlight.current = true;
    setFailure("");
    try {
      const result = await confirm({ variables: { input } });
      if (!result.data?.confirmCmpPublicEventPreCheckIn.preCheckedInAtUtcMilli) throw new Error("Confirmation unavailable");
      setConfirmed(true);
    } catch {
      setFailure("Your pre-check-in could not be confirmed. Refresh this page to check its status before trying again.");
    } finally { inFlight.current = false; }
  };
  return <main className="flex min-h-screen items-center justify-center bg-[#F7F7F8] px-5 py-10 text-[#0A0A0A]" style={{ fontFamily: "'DM Sans',sans-serif" }}>
    <section className="w-full max-w-lg rounded-xl border border-[#ECECEC] bg-white p-7 shadow-sm">
      <h1 className="text-2xl font-bold">{alreadyConfirmed ? "You are pre-checked in" : "Event pre-check-in"}</h1>
      {loading ? <p role="status" className="mt-4">Loading your event...</p> : error || !details ? <p role="alert" className="mt-4">This pre-check-in link does not work. It may have expired. Contact the chamber for help.</p> : <>
        <p className="mt-5 font-semibold">{details.eventName}</p>
        <p className="mt-2">{DateTime.fromMillis(details.startAtUtcMilli, { zone: details.timeZone }).toFormat("MMM d, yyyy 'at' h:mm a")}</p>
        <p className="mt-4">{details.attendeeName}</p>
        <p className="mt-4">{alreadyConfirmed ? "Your attendance is confirmed. The chamber will verify your check-in when you arrive." : "Select the button below to confirm your attendance and pre-check in."}</p>
        {!alreadyConfirmed && <button type="button" disabled={saving || !!failure} onClick={() => void submit()}
          className="mt-6 min-h-11 rounded-lg bg-[#E1A731] px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
          {saving ? "Confirming..." : "Confirm Attendance"}
        </button>}
        {failure && <p role="alert" className="mt-4 text-sm text-[#E24C4C]">{failure}</p>}
      </>}
    </section>
  </main>;
};
export default function EventPreCheckInPage() {
  const client = useMemo(() => createPublicApolloClient(), []);
  return <ApolloProvider client={client}><EventPreCheckInForm /></ApolloProvider>;
}
